import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { getTeamTheme } from "./teamTheme";
import "./variables.css";
import "./Standings.css";
import API_URL from "./config";

const Standings = () => {
  const navigate = useNavigate();
  const [standings, setStandings] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeConference, setActiveConference] = useState(null);

  useEffect(() => {
    const fetchStandings = async () => {
      setLoading(true);
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        let url = `${API_URL}/api/standings`;
        if (activeSaveId) {
          url += `?save_id=${activeSaveId}`;
        }

        const res = await fetch(url);
        if (!res.ok) {
          const data = await res.json();
          setError(data.detail || "Failed to load standings");
          return;
        }
        const data = await res.json();
        const s = data.standings ?? data;
        setStandings(s || {});
      } catch (err) {
        setError(err.message || "Server error");
      } finally {
        setLoading(false);
      }
    };
    fetchStandings();
  }, []);

  const winPercentage = (wins, losses) => {
    const total = (wins || 0) + (losses || 0);
    if (total === 0) return 0;
    return ((wins || 0) / total) * 100;
  };

  const winPctDisplay = (wins, losses) => {
    const total = (wins || 0) + (losses || 0);
    if (total === 0) return ".000";
    const ratio = (wins || 0) / total;
    if (ratio >= 1) return "1.000";
    return "." + Math.round(ratio * 1000).toString().padStart(3, "0");
  };

  const gamesBack = (leader, team) => {
    const gb =
      ((leader.wins || 0) - (team.wins || 0) + ((team.losses || 0) - (leader.losses || 0))) / 2;
    if (gb === 0) return "—";
    return gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1);
  };

  // Normalize entries
  const entries = Array.isArray(standings)
    ? [["League", standings]]
    : Object.entries(standings || {});

  // Set default active conference
  useEffect(() => {
    if (entries.length > 0 && activeConference === null) {
      setActiveConference("all");
    }
  }, [standings]);

  const filteredEntries =
    activeConference === "all"
      ? entries
      : entries.filter(([conf]) => conf === activeConference);

  // Conference totals for tab badges
  const confTeamCount = (conf) => {
    const entry = entries.find(([c]) => c === conf);
    return entry ? entry[1].length : 0;
  };

  return (
    <>
      <Navbar />
      <div className="standings-wrapper">
        {/* Hero Header */}
        <div className="standings-hero">
          <span className="standings-subtitle">2024-25 Season</span>
          <h2 className="standings-title">League Standings</h2>
        </div>

        {error && (
          <div className="standings-error">
            <span className="error-icon">!</span>
            {error}
          </div>
        )}

        {loading ? (
          <div className="standings-loading">
            <div className="loading-spinner" />
            <span>Loading standings...</span>
          </div>
        ) : (
          <>
            {/* Conference Tabs */}
            {entries.length > 1 && (
              <div className="conference-tabs">
                <button
                  className={`conf-tab ${activeConference === "all" ? "active" : ""}`}
                  onClick={() => setActiveConference("all")}
                >
                  All Conferences
                </button>
                {entries.map(([conf]) => (
                  <button
                    key={conf}
                    className={`conf-tab ${activeConference === conf ? "active" : ""}`}
                    onClick={() => setActiveConference(conf)}
                  >
                    {conf}
                    <span className="conf-tab-count">{confTeamCount(conf)}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="standings-container">
              {filteredEntries.length === 0 ? (
                <div className="standings-empty">
                  <div className="empty-icon">🏀</div>
                  <p>No standings available yet.</p>
                  <span>Play some games to see teams ranked here.</span>
                </div>
              ) : (
                filteredEntries.map(([conference, teams]) => (
                  <div key={conference} className="conference-section">
                    <div className="conference-header">
                      <h3 className="conference-name">{conference}</h3>
                      <span className="conference-team-count">
                        {(teams || []).length} Teams
                      </span>
                    </div>

                    <div className="standings-table-wrapper">
                      <table className="standings-table">
                        <thead>
                          <tr>
                            <th className="col-rank">#</th>
                            <th className="col-team">Team</th>
                            <th className="col-stat">W</th>
                            <th className="col-stat">L</th>
                            <th className="col-stat">PCT</th>
                            <th className="col-stat col-gb">GB</th>
                            <th className="col-bar">Win %</th>
                            <th className="col-arrow"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(teams || []).map((t, i) => {
                            const theme = getTeamTheme(t.name || t._id);
                            const pct = winPercentage(t.wins, t.losses);
                            const leader = teams[0];
                            const animDelay = `${i * 0.04}s`;

                            return (
                              <tr
                                key={t._id}
                                className="standings-row"
                                style={{ animationDelay: animDelay }}
                                onClick={() => navigate(`/teams?teamId=${t._id}`)}
                              >
                                <td className="col-rank">
                                  <span
                                    className={`rank-badge ${
                                      i < 3 ? `rank-${i + 1}` : ""
                                    }`}
                                  >
                                    {i + 1}
                                  </span>
                                </td>
                                <td className="col-team">
                                  <div className="team-info">
                                    <span
                                      className="team-color-dot"
                                      style={{ background: theme.primary }}
                                    />
                                    <span className="team-name-text">
                                      {t.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="col-stat wins-cell">
                                  {t.wins || 0}
                                </td>
                                <td className="col-stat losses-cell">
                                  {t.losses || 0}
                                </td>
                                <td className="col-stat pct-cell">
                                  {winPctDisplay(t.wins, t.losses)}
                                </td>
                                <td className="col-stat col-gb gb-cell">
                                  {gamesBack(leader, t)}
                                </td>
                                <td className="col-bar">
                                  <div className="win-bar-track">
                                    <div
                                      className="win-bar-fill"
                                      style={{
                                        width: `${pct}%`,
                                        background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                                        animationDelay: animDelay,
                                      }}
                                    />
                                  </div>
                                </td>
                                <td className="col-arrow">
                                  <span className="row-arrow">›</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Standings;
